/**
 * How a tool RESULT is rendered in the chat transcript (18.08.2026).
 *
 * Sibling to toolPresentation.js, which describes the *call*. This one
 * describes what came back. Everything without an entry here keeps falling
 * through to the collapsed JSON block in ToolCallCard, so this map is
 * deliberately sparse and can be grown one tool at a time.
 *
 * Contract: (toolResult, toolInput, { interactive }) => JSX | null.
 * Returning null means "I can't render this" and hands the result back to the
 * generic JSON block — which is the right answer for an unexpected shape.
 * A renderer must never throw; renderToolResult catches, but a renderer that
 * relies on that is a renderer that will silently render nothing.
 *
 * Why these three first: they are the highest-traffic results, and two of them
 * (send_request, db_run_sql) previously had a readable card ONLY when
 * interactive mode was on. With it off — the default for agentic runs — they
 * rendered as raw JSON. The renderers below cover both modes, which is why the
 * interactive-only branches for them came out of ToolCallCard: one code path
 * instead of two that drift.
 */
import React from 'react';
import { Loader, ShieldAlert } from 'lucide-react';
import MonacoReadOnly from '../ui/MonacoReadOnly';

/** Trim a value to something that fits a card row. */
const clip = (value, max = 90) => {
  const text = String(value ?? '').replace(/\s+/g, ' ').trim();
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

/**
 * Colour bucket for an HTTP status.
 *
 * Real class names, not `dark:` utilities: index.css is a hand-rolled utility
 * subset and several `dark:text-*` variants used elsewhere in this panel are
 * not defined at all, so they render light-mode colours in dark mode.
 */
function statusTone(status) {
  const code = Number(status);
  if (!Number.isFinite(code)) return 'muted';
  if (code >= 500) return 'err';
  if (code >= 400) return 'warn';
  if (code >= 300) return 'info';
  if (code >= 200) return 'ok';
  return 'muted';
}

/** Bytes → a short human string. Sizes land in a 90px-ish column. */
function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** A content-type header down to its essential token. */
function shortContentType(contentType) {
  if (!contentType) return null;
  return String(contentType).split(';')[0].trim() || null;
}

function PendingLine({ children }) {
  return (
    <div className="ai-chat-result-line">
      <Loader size={12} className="animate-spin flex-shrink-0" />
      <span>{children}</span>
    </div>
  );
}

function ErrorLine({ toolName, message }) {
  return (
    <div className="ai-chat-result-line ai-chat-result-line--err">
      <span className="ai-chat-result-tool">{toolName}</span> failed: {message}
    </div>
  );
}

function CancelledLine() {
  return <div className="ai-chat-result-line ai-chat-result-line--muted">Cancelled</div>;
}

/**
 * Shared prelude: the three non-result states every tool shares.
 *
 * Returns JSX for pending / error / cancelled, `null` when there is nothing to
 * render at all, and `false` when the result is a real one the caller should
 * render itself. `false` rather than undefined so the caller's check is an
 * explicit identity test and a renderer can't fall through by accident.
 */
function commonStates(toolName, result, pendingText) {
  if (result === undefined) return null;
  if (result === null) return <PendingLine>{pendingText}</PendingLine>;
  if (result.error) return <ErrorLine toolName={toolName} message={clip(result.error, 200)} />;
  if (result.cancelled) return <CancelledLine />;
  return false;
}

/**
 * db_run_sql — the shape of the result set, never its rows.
 *
 * The rows genuinely are not here to render: the executor returns
 * { executed, row_count, columns } and the rows only ever exist in the DB
 * Manager grid. That is the enforcement mechanism for "the model never sees
 * query results" — it is structural, not a projection that could be missed —
 * so this renders the columns and the count and points at the grid.
 */
function DbRunSqlResult(result, input, { interactive }) {
  const columns = Array.isArray(result.columns) ? result.columns : [];
  const rowCount = result.row_count ?? 0;
  const where = interactive ? 'DB Manager grid' : 'DB Manager';
  const hasTypes = columns.some((c) => c && c.type);

  if (columns.length === 0) {
    // DDL/DML: no result set, so the count is an affected-row count.
    return (
      <div className="ai-chat-result">
        <div className="ai-chat-result-line">
          <span className="ai-chat-result-tool">db_run_sql</span>
          {' → '}
          <span className="ai-chat-result-ok">{rowCount} row(s) affected</span>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-chat-result">
      <div className="ai-chat-result-line">
        <span className="ai-chat-result-tool">db_run_sql</span>
        {' → '}
        <span className="ai-chat-result-ok">{rowCount} row(s)</span>
        <span className="ai-chat-result-meta">
          · {columns.length} column{columns.length === 1 ? '' : 's'} · rows shown in the {where}
        </span>
      </div>
      {/* Own scroll container: the chat panel is resizable down to a small
          minimum width, and a table that doesn't scroll inside itself pushes
          the whole panel sideways. */}
      <div className="ai-chat-result-table-wrap">
        <table className="ai-chat-result-table">
          <thead>
            <tr>
              <th scope="col">Column</th>
              {hasTypes && <th scope="col">Type</th>}
            </tr>
          </thead>
          <tbody>
            {columns.map((col, i) => (
              <tr key={`${col?.name ?? ''}-${i}`}>
                <td>{clip(col?.name, 48) || '—'}</td>
                {hasTypes && <td className="ai-chat-result-meta">{clip(col?.type, 24) || '—'}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * update_request — what actually changed, before → after.
 *
 * `changes` is populated by the executor (useAiActions.js) and is UI-side
 * only: condenseToolResultForApi projects update_request down to
 * { success, request_id, updated_fields }, so these values never reach the
 * model. Header VALUES are already reduced to key names at the source, since
 * that is where credentials live.
 *
 * Falls back to `updated_fields` when `changes` is absent, so a result
 * captured before the executor change still renders as a list rather than
 * dropping to raw JSON.
 */
function UpdateRequestResult(result, input) {
  const changes = Array.isArray(result.changes) ? result.changes : null;
  const fields = Array.isArray(result.updated_fields) ? result.updated_fields : [];

  if (!changes && fields.length === 0) return null;

  return (
    <div className="ai-chat-result">
      <div className="ai-chat-result-line">
        <span className="ai-chat-result-tool">update_request</span>
        <span className="ai-chat-result-meta">
          · {(changes || fields).length} field{(changes || fields).length === 1 ? '' : 's'} updated
          {result.request?.name ? ` on ${clip(result.request.name, 40)}` : ''}
        </span>
      </div>
      <div className="ai-chat-result-diff">
        {(changes || fields.map((field) => ({ field }))).map((change, i) => (
          <div className="ai-chat-result-diff-row" key={`${change.field}-${i}`}>
            <span className="ai-chat-result-diff-field">{change.field}</span>
            {change.before !== undefined || change.after !== undefined ? (
              <span className="ai-chat-result-diff-values">
                <span className="ai-chat-result-diff-before">{clip(change.before) || '∅'}</span>
                <span className="ai-chat-result-diff-arrow">→</span>
                <span className="ai-chat-result-diff-after">{clip(change.after) || '∅'}</span>
              </span>
            ) : (
              <span className="ai-chat-result-meta">changed</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** send_request — the status pill, in both interactive and headless mode. */
function SendRequestResult(result, input, { interactive }) {
  if (result.status === undefined || result.status === null) return null;

  const target = input?.use_current_tab
    ? 'active tab'
    : input?.request_id
      ? `request ${clip(input.request_id, 24)}`
      : clip(`${input?.method || 'GET'} ${input?.url || ''}`.trim(), 60);
  const tone = statusTone(result.status);
  const size = formatBytes(result.size_bytes);
  const type = shortContentType(result.content_type);

  return (
    <div className="ai-chat-result">
      <div className="ai-chat-result-line">
        <span className={`ai-chat-pill ai-chat-pill--${tone}`}>
          {result.status}
          {result.status_text ? ` ${clip(result.status_text, 24)}` : ''}
        </span>
        <span className="ai-chat-result-target">{target}</span>
        <span className="ai-chat-result-meta">
          {result.time_ms != null && ` · ${result.time_ms}ms`}
          {size && ` · ${size}`}
          {type && ` · ${type}`}
          {interactive && ' · Request Builder'}
        </span>
      </div>
    </div>
  );
}

/** generate_code's language enum → the Monaco language id. */
const CODE_EDITOR_LANGUAGES = {
  curl: 'shell',
  python: 'python',
  typescript: 'typescript',
  go: 'go',
  java: 'java',
  csharp: 'csharp',
  php: 'php',
  ruby: 'ruby',
  swift: 'swift',
};

/**
 * generate_code — the snippet itself, syntax-highlighted.
 *
 * Without this the card renders the result object as JSON, which means a
 * multi-line snippet arrives as one line of "\n"-escaped string. The redaction
 * notice is not decoration: a snippet with <SECRET_REDACTED> in it does not
 * run, and the user needs to know that before they paste it.
 */
function GenerateCodeResult(result) {
  if (typeof result.code !== 'string' || !result.code) return null;

  return (
    <div className="ai-chat-result">
      <div className="ai-chat-result-line">
        <span className="ai-chat-result-tool">generate_code</span>
        <span className="ai-chat-result-meta">
          {result.library ? `· ${result.library}` : ''}
          {result.method ? ` · ${result.method}` : ''}
          {result.request_name ? ` · ${clip(result.request_name, 40)}` : ''}
        </span>
      </div>
      {result.secrets_redacted && (
        <div className="ai-chat-result-line ai-chat-result-line--warn">
          <ShieldAlert size={12} className="flex-shrink-0" />
          <span>Credentials replaced with &lt;SECRET_REDACTED&gt; — fill them in before running.</span>
        </div>
      )}
      <MonacoReadOnly
        value={result.code}
        language={CODE_EDITOR_LANGUAGES[result.language] || 'text'}
        // 180, not more: .ai-chat-code-block caps the container at 180px with
        // overflow:hidden, so a taller editor has its last lines — and its own
        // scrollbar — clipped with no way to reach them.
        maxHeight={180}
        className="ai-chat-code-block ai-chat-result-code"
      />
    </div>
  );
}

/**
 * create_mock_example — what was saved, and who can read it.
 *
 * The warning is not decoration. The local mock server hard-codes
 * `Access-Control-Allow-Origin: *` and takes no credential (src-tauri/src/
 * mock_server.rs), so anything it serves is readable by any page open in the
 * user's browser. Mocking a literal payload the user wrote is unremarkable;
 * mocking a live production response quietly republishes it, and the moment to
 * say so is the moment it happens.
 */
function CreateMockExampleResult(result) {
  if (!result.example_id) return null;

  return (
    <div className="ai-chat-result">
      <div className="ai-chat-result-line">
        <span className="ai-chat-result-tool">create_mock_example</span>
        {' → '}
        <span className="ai-chat-result-ok">{clip(result.name, 40)}</span>
        <span className="ai-chat-result-meta">
          · {result.status}
          {result.body_bytes != null && ` · ${formatBytes(result.body_bytes) || `${result.body_bytes} B`}`}
          {result.route_path ? ` · ${clip(result.route_path, 40)}` : ''}
        </span>
      </div>
      {result.copied_from_live_response && (
        <div className="ai-chat-result-line ai-chat-result-line--warn">
          <ShieldAlert size={12} className="flex-shrink-0" />
          <span>
            Copied from a real response. The mock server has no authentication and
            allows any origin, so any page open in your browser can read it.
          </span>
        </div>
      )}
      {result.random_between > 1 && (
        <div className="ai-chat-result-line ai-chat-result-line--warn">
          <span>
            {result.random_between} examples on this route have no match conditions —
            the server picks between them at random.
          </span>
        </div>
      )}
      {result.mock_url && (
        <div className="ai-chat-result-line ai-chat-result-meta">{clip(result.mock_url, 80)}</div>
      )}
      {result.mock_server_running === false && (
        <div className="ai-chat-result-line ai-chat-result-meta">
          Saved — start the Mock server to serve it.
        </div>
      )}
    </div>
  );
}

/**
 * create_folders — counts plus one line per failed item.
 *
 * The batch result carries the whole report (ids, paths, flags); the card
 * shows what the user cares about — how many landed, how many were already
 * there, and which ones did not and why. Ids are noise here; they are for the
 * model's next call.
 */
function CreateFoldersResult(result) {
  if (!Array.isArray(result.folders)) return null;
  const failed = result.folders.filter((f) => f && f.error);
  const created = result.created_count ?? 0;
  const existing = result.existing_count ?? 0;
  const where = result.collection_name ? ` in ${clip(result.collection_name, 40)}` : '';

  return (
    <div className="ai-chat-result">
      <div className="ai-chat-result-line">
        <span className="ai-chat-result-tool">create_folders</span>
        {' → '}
        <span className="ai-chat-result-ok">{created} folder{created === 1 ? '' : 's'} created{where}</span>
        <span className="ai-chat-result-meta">
          {existing > 0 && ` · ${existing} already existed`}
          {failed.length > 0 && ` · ${failed.length} failed`}
        </span>
      </div>
      {failed.map((f) => (
        <div className="ai-chat-result-line ai-chat-result-line--err" key={`fail-${f.index}`}>
          <span className="ai-chat-result-target">{clip(f.name, 40) || `item ${f.index + 1}`}</span>
          <span>{clip(f.error, 120)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * web_search — the hits as a plain list: title, url, snippet.
 *
 * No clickable links: the chat is not a browser, and a model-chosen URL is
 * exactly the kind of thing a user should read before opening.
 */
function WebSearchResult(result) {
  if (!Array.isArray(result.results)) return null;
  const hits = result.results;

  return (
    <div className="ai-chat-result">
      <div className="ai-chat-result-line">
        <span className="ai-chat-result-tool">web_search</span>
        {' → '}
        <span className="ai-chat-result-ok">{hits.length} result{hits.length === 1 ? '' : 's'}</span>
        {result.query && <span className="ai-chat-result-meta">· {clip(result.query, 60)}</span>}
      </div>
      {hits.length === 0 && (
        <div className="ai-chat-result-line ai-chat-result-line--muted">No results</div>
      )}
      {hits.map((hit, i) => (
        <div className="ai-chat-result-line" key={`${hit?.url || ''}-${i}`}>
          <span className="ai-chat-result-target">{clip(hit?.title, 80) || clip(hit?.url, 80)}</span>
          <span className="ai-chat-result-meta">{clip(hit?.url, 90)}</span>
          {hit?.snippet && <span className="ai-chat-result-meta">{clip(hit.snippet, 160)}</span>}
        </div>
      ))}
    </div>
  );
}

/**
 * fetch_url — what was fetched and how much, never the page text.
 *
 * The text goes to the model (bounded, scrubbed); the transcript shows the
 * title, the size and the link count so the user can see what left their
 * machine without the page body scrolling through the chat.
 */
function FetchUrlResult(result) {
  if (!result.url) return null;
  const size = formatBytes(result.total_chars);
  const links = result.link_count ?? (Array.isArray(result.links) ? result.links.length : 0);

  return (
    <div className="ai-chat-result">
      <div className="ai-chat-result-line">
        <span className="ai-chat-result-tool">fetch_url</span>
        {' → '}
        <span className="ai-chat-result-ok">Fetched {clip(result.title, 60) || clip(result.url, 60)}</span>
        <span className="ai-chat-result-meta">
          {size && ` · ${size}`}
          {` · ${links} link${links === 1 ? '' : 's'}`}
          {result.truncated && ' · truncated'}
        </span>
      </div>
      {result.looks_like_api_spec && (
        <div className="ai-chat-result-line ai-chat-result-line--warn">
          <span>Looks like an API spec ({result.spec_hint || 'spec'}) — import it with import_spec rather than reading it.</span>
        </div>
      )}
      <div className="ai-chat-result-line ai-chat-result-meta">{clip(result.url, 90)}</div>
    </div>
  );
}

/** tool name → result renderer. Sparse on purpose; see the file header. */
export const TOOL_RESULT_RENDERERS = {
  create_folders: {
    pending: (input) => `Creating ${Array.isArray(input?.folders) ? input.folders.length : 0} folder(s)…`,
    render: CreateFoldersResult,
  },
  web_search: {
    pending: (input) => `Searching ${input?.query ? `"${clip(input.query, 40)}"` : 'the web'}…`,
    render: WebSearchResult,
  },
  fetch_url: {
    pending: (input) => `Fetching ${clip(input?.url, 60) || 'page'}…`,
    render: FetchUrlResult,
  },
  db_run_sql: {
    pending: (input, { interactive }) => (interactive ? 'Running SQL in DB Manager…' : 'Running SQL…'),
    render: DbRunSqlResult,
  },
  update_request: {
    pending: () => 'Updating request…',
    render: UpdateRequestResult,
  },
  create_mock_example: {
    pending: () => 'Saving mock example…',
    render: CreateMockExampleResult,
  },
  generate_code: {
    pending: (input) => `Generating ${String(input?.language || 'code')}…`,
    render: GenerateCodeResult,
  },
  send_request: {
    pending: (input, { interactive }) => {
      const what = input?.use_current_tab
        ? 'active tab'
        : input?.request_id
          ? 'saved request'
          : clip(`${input?.method || 'GET'} ${input?.url || ''}`.trim(), 60);
      return interactive ? `Sending ${what} in Request Builder…` : `Sending ${what}…`;
    },
    render: SendRequestResult,
  },
};

/**
 * Render a tool result richly, or return null to use the generic JSON block.
 *
 * The try/catch is the same defence describeToolCall has: a malformed result
 * must not take the whole transcript down with it. A throw falls back to the
 * JSON block, which still shows the user everything that came back.
 */
export function renderToolResult(toolName, toolResult, toolInput, options = {}) {
  const entry = TOOL_RESULT_RENDERERS[toolName];
  if (!entry) return null;
  const ctx = { interactive: Boolean(options.interactive) };

  try {
    const pendingText = entry.pending ? entry.pending(toolInput || {}, ctx) : 'Running…';
    const common = commonStates(toolName, toolResult, pendingText);
    if (common !== false) return common;
    return entry.render(toolResult, toolInput || {}, ctx) ?? null;
  } catch {
    return null;
  }
}
